import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Music, Copy, RefreshCw } from 'lucide-react';
import {
  getMusicConfig,
  saveMusicConfig,
  regenerateMusicKey,
  MusicConfig,
  MusicMode,
} from '../../../api/payment/music-request.api';
import MusicTierEditor from '../../../components/music-request/music-tier-editor';
import VolumeSlider from '../../../components/volume-slider/volume-slider';
import { normalizeVolume } from '../../../utils/overlay-volume';
import {
  resolveTiers,
  rowsToTiers,
  sortTierRows,
  tiersToRows,
  TierRow,
  validateTierRows,
} from '../../../utils/music-request';

// "Phát nhạc theo yêu cầu": người xem trả Ca kèm link YouTube, streamer duyệt (mặc định)
// hoặc để tự phát; bài được phát trên overlay livestream qua trình nhúng YouTube chính thức.

const MIN_PENDING = 1;
const MAX_PENDING = 10;

interface FormState {
  isEnabled: boolean;
  tiers: TierRow[];
  mode: MusicMode;
  maxPendingPerViewer: string;
  volume: number;
}

/**
 * Ép số nguyên ngay lúc gõ (không chờ đến Lưu): làm tròn, kẹp trong khoảng cho phép — để
 * `maxSeconds` gửi lên API luôn là số nguyên giây, không lỡ gửi số thập phân từ input number.
 * Ô trống vẫn cho phép trống tạm thời để người dùng xoá rồi gõ lại.
 */
const toIntString = (raw: string, min?: number, max?: number): string => {
  if (raw === '') return '';
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return '';
  let clamped = n;
  if (min !== undefined) clamped = Math.max(min, clamped);
  if (max !== undefined) clamped = Math.min(max, clamped);
  return `${clamped}`;
};

const toForm = (c: MusicConfig): FormState => ({
  isEnabled: c.isEnabled,
  tiers: tiersToRows(resolveTiers(c.tiers, c.price, c.maxSeconds)),
  mode: c.mode,
  maxPendingPerViewer: `${c.maxPendingPerViewer}`,
  volume: normalizeVolume(c.volume),
});

const MusicRequestSetting: React.FC<{ userId?: string }> = ({ userId }) => {
  const { host, protocol } = window.location;
  const [config, setConfig] = useState<MusicConfig | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = () => {
    getMusicConfig()
      .then((res) => {
        setConfig(res);
        setForm(toForm(res));
        setLoadError(false);
      })
      .catch(() => {
        setLoadError(true);
        toast.error('Không tải được cài đặt phát nhạc.');
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const overlayUrl = `${protocol}//${host}/music-screen/${userId}?key=${config?.overlayKey || ''}`;
  const controlUrl = `${protocol}//${host}/phat-nhac/dieu-khien`;

  const handleSave = () => {
    if (saving || !form) return; // chặn bấm 2 lần
    const sortedRows = sortTierRows(form.tiers);
    setForm((f) => (f ? { ...f, tiers: sortedRows } : f));
    const tierError = validateTierRows(sortedRows);
    if (tierError) {
      toast.error(tierError);
      return;
    }
    const tiers = rowsToTiers(sortedRows);
    const maxPendingPerViewer = Number(form.maxPendingPerViewer);
    if (!maxPendingPerViewer || maxPendingPerViewer < MIN_PENDING || maxPendingPerViewer > MAX_PENDING) {
      toast.error(`Số bài chờ tối đa mỗi người phải từ ${MIN_PENDING} đến ${MAX_PENDING}.`);
      return;
    }

    // Âm lượng chỉ gửi khi đổi ở đây, để không ghi đè số streamer vừa chỉnh ở bảng điều khiển trong lúc trang này mở.
    const volumeChanged = form.volume !== normalizeVolume(config?.volume);

    setSaving(true);
    saveMusicConfig({
      isEnabled: form.isEnabled,
      price: tiers[0].price,
      mode: form.mode,
      maxSeconds: tiers[tiers.length - 1].seconds,
      tiers,
      maxPendingPerViewer,
      ...(volumeChanged ? { volume: form.volume } : {}),
    })
      .then((res) => {
        setConfig(res);
        setForm(toForm(res));
        toast.success('Đã lưu cài đặt phát nhạc');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Lưu cài đặt thất bại');
      })
      .finally(() => setSaving(false));
  };

  const handleRegenerateKey = () => {
    if (regenerating) return;
    if (!window.confirm('Tạo link overlay mới? Link overlay cũ sẽ ngưng hoạt động ngay.')) return;
    setRegenerating(true);
    regenerateMusicKey()
      .then((res) => {
        setConfig(res);
        toast.success('Đã tạo link overlay mới');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Tạo link mới thất bại');
      })
      .finally(() => setRegenerating(false));
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Đã sao chép link');
    } catch {
      toast.error('Trình duyệt chặn sao chép, hãy bôi đen link rồi tự chép lại.');
    }
  };

  const inputCls =
    'h-11 rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-base dark:text-white';

  return (
    <div className="border rounded-xl border-[#DEE0E6] dark:border-slate-700 mt-[10px]">
      <p className="p-3 flex items-center gap-2 w-full text-[#18191E] dark:text-white font-semibold text-[22px] leading-[27px]">
        <Music size={20} className="text-blue-600" />
        Phát nhạc theo yêu cầu
      </p>

      <div className="w-full flex flex-col gap-3 bg-[#f6f7f8] dark:bg-dark2 p-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Người xem trả Ca kèm link YouTube để yêu cầu phát bài trên phiên live của bạn. Bạn có
          thể duyệt trước từng bài, hoặc để bài tự vào hàng chờ.
        </p>

        {loadError && !config && (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-3">
            <span className="text-sm text-red-600 dark:text-red-400">
              Không tải được cài đặt phát nhạc.
            </span>
            <button
              type="button"
              onClick={load}
              className="shrink-0 rounded-lg border border-red-300 dark:border-red-700 px-3 py-1 text-sm text-red-600 dark:text-red-400"
            >
              Thử lại
            </button>
          </div>
        )}

        {form && (
          <>
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.isEnabled}
                onChange={(e) => setForm((f) => (f ? { ...f, isEnabled: e.target.checked } : f))}
                className="mt-[3px] h-4 w-4 shrink-0 rounded border-[#C8CCD8] accent-blue-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Bật nhận yêu cầu phát nhạc
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  Tắt thì trang cá nhân của bạn ẩn khối gửi nhạc, hàng chờ hiện có vẫn phát hết.
                </span>
              </span>
            </label>

            <MusicTierEditor
              rows={form.tiers}
              onChange={(tiers) => setForm((f) => (f ? { ...f, tiers } : f))}
            />

            <div className="flex flex-col gap-1">
              <span className="text-sm">Chế độ</span>
              <div className="flex flex-col gap-2">
                <label className="flex items-start gap-2 cursor-pointer select-none text-sm">
                  <input
                    type="radio"
                    name="music-mode"
                    className="mt-[3px] accent-blue-600"
                    checked={form.mode === 'Approve'}
                    onChange={() => setForm((f) => (f ? { ...f, mode: 'Approve' } : f))}
                  />
                  <span>
                    Duyệt trước
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      Bài vào danh sách chờ duyệt; người xem chỉ bị trừ Ca khi bạn duyệt.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer select-none text-sm">
                  <input
                    type="radio"
                    name="music-mode"
                    className="mt-[3px] accent-blue-600"
                    checked={form.mode === 'Auto'}
                    onChange={() => setForm((f) => (f ? { ...f, mode: 'Auto' } : f))}
                  />
                  <span>
                    Tự phát
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      Bài vào hàng chờ ngay, người xem bị trừ Ca ngay lúc gửi.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="music-max-pending" className="text-sm">
                Số bài chờ tối đa mỗi người xem
              </label>
              <input
                id="music-max-pending"
                type="number"
                min={MIN_PENDING}
                max={MAX_PENDING}
                step={1}
                value={form.maxPendingPerViewer}
                onChange={(e) =>
                  setForm((f) =>
                    f
                      ? {
                          ...f,
                          maxPendingPerViewer: toIntString(e.target.value, MIN_PENDING, MAX_PENDING),
                        }
                      : f
                  )
                }
                className={`${inputCls} w-full`}
              />
            </div>

            <VolumeSlider
              id="music-volume"
              label="Âm lượng nhạc"
              value={form.volume}
              onChange={(volume) => setForm((f) => (f ? { ...f, volume } : f))}
              hint="Bạn có thể chỉnh nhỏ lại khi vừa chơi vừa phát trực tiếp. Sau khi bạn lưu, OBS sẽ tự cập nhật trong vài giây mà không cần tải lại. Bảng điều khiển phát nhạc cũng có thanh này để bạn chỉnh nhanh trong lúc phát trực tiếp."
            />

            <div className="flex flex-col gap-1">
              <h2 className="text-sm">Link hiển thị phát nhạc ( Add Webpage URL trong phần mềm Stream )</h2>
              <div className="flex items-center gap-2">
                <div className="flex h-12 min-w-0 flex-1 items-center rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-[#EBECF2] dark:bg-slate-700 px-3 text-base text-[#A1A5B6] dark:text-slate-400">
                  <span className="w-full truncate">{overlayUrl}</span>
                </div>
                <button
                  type="button"
                  onClick={() => copyLink(overlayUrl)}
                  title="Sao chép link"
                  className="flex h-12 shrink-0 items-center gap-1 rounded-lg bg-[#39539E] px-4 text-white"
                >
                  <Copy size={16} />
                  Sao chép
                </button>
              </div>
              <button
                type="button"
                onClick={handleRegenerateKey}
                disabled={regenerating}
                className="mt-1 flex w-fit items-center gap-1 rounded-lg border border-[#C8CCD8] dark:border-slate-600 px-3 py-2 text-sm dark:text-white disabled:opacity-60"
              >
                <RefreshCw size={14} />
                {regenerating ? 'Đang tạo…' : 'Tạo link mới'}
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Tạo link mới nếu link cũ bị lộ — link cũ ngưng hoạt động ngay.
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <h2 className="text-sm">Bảng điều khiển duyệt bài</h2>
              <div className="flex items-center gap-2">
                <div className="flex h-12 min-w-0 flex-1 items-center rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-[#EBECF2] dark:bg-slate-700 px-3 text-base text-[#A1A5B6] dark:text-slate-400">
                  <span className="w-full truncate">{controlUrl}</span>
                </div>
                <a
                  href="/phat-nhac/dieu-khien"
                  className="flex h-12 shrink-0 items-center gap-1 rounded-lg bg-[#39539E] px-4 text-white"
                >
                  Mở
                </a>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Mở trang này để duyệt bài, xem hàng chờ và tạm dừng phát (bài đang phát dừng lại,
                hàng chờ giữ nguyên).
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-lg bg-blue-500 px-4 py-2 text-base text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? 'Đang lưu…' : 'Lưu cài đặt'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MusicRequestSetting;
